/**
 * i18next, wired to the tenant's locales rather than the browser's.
 *
 * The server already negotiated a locale for this session — `?locale=` →
 * `Accept-Language` → the user's stored preference → the tenant default —
 * and reports the answer as `resolved_locale` on `/auth/me`. Letting
 * i18next detect independently would give a UI in one language and record
 * data in another, which is worse than either alone.
 */

import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import en from "@/locales/en/common.json";
import hi from "@/locales/hi/common.json";

export const FALLBACK_LOCALE = "en";

export const resources = {
  en: { common: en },
  hi: { common: hi },
} as const;

export type SupportedLocale = keyof typeof resources;

export function isSupported(locale: string): locale is SupportedLocale {
  return Object.hasOwn(resources, locale);
}

export async function initI18n(locale: string = FALLBACK_LOCALE): Promise<void> {
  if (i18next.isInitialized) {
    await setLocale(locale);
    return;
  }
  await i18next.use(initReactI18next).init({
    resources,
    lng: isSupported(locale) ? locale : FALLBACK_LOCALE,
    fallbackLng: FALLBACK_LOCALE,
    defaultNS: "common",
    interpolation: { escapeValue: false },
    // React already escapes, and i18next's own escaping would double-encode
    // anything with an ampersand in it.
    react: { useSuspense: false },
  });
}

/**
 * Switch language, and tell assistive technology.
 *
 * `<html lang>` is the half everybody forgets: a screen reader picks its
 * pronunciation from it, so a Hindi page still announcing `lang="en"` is
 * read out in an English accent. Setting it here means the one function
 * that changes language cannot change one without the other.
 */
export async function setLocale(locale: string): Promise<void> {
  const next = isSupported(locale) ? locale : FALLBACK_LOCALE;
  if (i18next.language !== next) await i18next.changeLanguage(next);
  document.documentElement.lang = next;
}
